import {
  Box,
  Stack,
  Skeleton,
  Alert,
  Card,
  CardContent,
  Typography,
  Link,
  LinearProgress,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import TagIcon from '@mui/icons-material/Tag';
import { useNavigate } from 'react-router-dom';
import { PermissionGate } from '@billda/permissions-ui';
import { useSiteDetails } from '../../../sites/hooks/useSiteDetails';
import { formatCurrency } from '../../../../shared/utils/currency';
import { What3WordsLink } from '../../../../shared/components/atoms/What3WordsLink';
import { SiteEventsTimeline } from '../../../sites/components/timeline/SiteEventsTimeline';

interface SiteDrawerContentProps {
  siteId: number;
}

export function SiteDrawerContent({ siteId }: SiteDrawerContentProps) {
  const navigate = useNavigate();
  const { data: site, error, isFetching: isLoading } = useSiteDetails(siteId);

  if (isLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Stack spacing={2}>
          <Skeleton variant="rectangular" height={150} />
          <Skeleton variant="rectangular" height={100} />
        </Stack>
      </Box>
    );
  }

  if (error || !site) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          {error instanceof Error
            ? error.message
            : 'Failed to load site details'}
        </Alert>
      </Box>
    );
  }

  const plotCount = site.plots?.length ?? 0;
  const labourItemCount = 0;

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={3}>
        {/* Site Overview Card */}
        <Card variant="outlined">
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <BusinessIcon sx={{ fontSize: 40, color: 'primary.main' }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="h6">{site.name}</Typography>
                {site.slug && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                    }}
                    data-testid="site-slug"
                  >
                    <TagIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      {site.slug}
                    </Typography>
                  </Box>
                )}
                {site.what3WordsAddress && (
                  <What3WordsLink address={site.what3WordsAddress} />
                )}
              </Box>
            </Box>

            <Link
              component="button"
              onClick={() => navigate(`/sites/${siteId}`)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                cursor: 'pointer',
                mb: 2,
              }}
            >
              View full site details
              <OpenInNewIcon fontSize="small" />
            </Link>

            <Stack spacing={1}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">
                  Plots
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {plotCount}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">
                  Labour Items
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {labourItemCount}
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        {/* Financial Metrics - Permission Gated */}
        <PermissionGate
          permission="sites.financial.read"
          fallback={null}
          showRequiredPermissions={false}
        >
          {site.financialMetrics && (
            <Card variant="outlined">
              <CardContent>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  gutterBottom
                >
                  Financial Summary
                </Typography>

                {/* Progress Bar */}
                <Box sx={{ mb: 2 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      mb: 0.5,
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Progress
                    </Typography>
                    <Typography variant="caption" fontWeight="medium">
                      {site.financialMetrics.progress.toFixed(1)}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(site.financialMetrics.progress, 100)}
                    sx={{ height: 8, borderRadius: 1 }}
                  />
                </Box>

                <Stack spacing={1}>
                  <Box
                    sx={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Total Value
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {formatCurrency(site.financialMetrics.totalLabourPrice)}
                    </Typography>
                  </Box>
                  <Box
                    sx={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Approved
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {formatCurrency(site.financialMetrics.approvedAmount)}
                    </Typography>
                  </Box>
                  <Box
                    sx={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Booked
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {formatCurrency(site.financialMetrics.bookedAmount)}
                    </Typography>
                  </Box>
                  <Box
                    sx={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Variations
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {formatCurrency(site.financialMetrics.variationsAmount)}
                    </Typography>
                  </Box>
                  <Box
                    sx={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Remaining
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {formatCurrency(site.financialMetrics.remaining)}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          )}
        </PermissionGate>

        {/* Site History Timeline */}
        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Site History
            </Typography>
            <SiteEventsTimeline siteId={siteId} />
          </CardContent>
        </Card>
      </Stack>
    </Box>
  );
}
